import type { ProductGeometry } from '../types.ts'
import type {
  DesignCanvas, DesignDocument, Frame, ImageLayer, LayerConstraints, ShapeLayer, StickerLayer, TextLayer,
} from './types.ts'
import { DESIGN_SCHEMA_VERSION } from './types.ts'

let idCounter = 0
/** Deterministische, dokumentlokale IDs (kein Math.random: Render muss reproduzierbar sein). */
export function nextLayerId(prefix = 'l', existing?: Iterable<string>): string {
  const taken = new Set(existing ?? [])
  let id = ''
  do {
    idCounter += 1
    id = `${prefix}${idCounter}`
  } while (taken.has(id))
  return id
}

export function resetLayerIdCounter(): void {
  idCounter = 0
}

export function defaultConstraints(patch: Partial<LayerConstraints> = {}): LayerConstraints {
  return { locked: false, contentOnly: false, optional: true, ...patch }
}

export function canvasFromGeometry(g: ProductGeometry, background: DesignCanvas['background'] = { type: 'color', value: '#FFFFFF' }): DesignCanvas {
  return {
    widthMm: g.trimWidthMm,
    heightMm: g.trimHeightMm,
    bleedMm: g.bleedMm,
    safeMarginMm: g.safeMarginMm,
    dpi: g.dpi,
    shape: g.shape,
    cornerRadiusMm: g.cornerRadiusMm,
    shapeParams: g.shapeParams ?? null,
    background,
    colorProfile: 'sRGB',
  }
}

/** Rahmen, der das volle Format (Endformat + Beschnitt) abdeckt. */
export function fullBleedFrame(canvas: Pick<DesignCanvas, 'widthMm' | 'heightMm' | 'bleedMm'>): Frame {
  return {
    xMm: -canvas.bleedMm,
    yMm: -canvas.bleedMm,
    wMm: canvas.widthMm + 2 * canvas.bleedMm,
    hMm: canvas.heightMm + 2 * canvas.bleedMm,
    rotationDeg: 0,
  }
}

export function createImageLayer(id: string, frame: Frame, patch: Partial<Omit<ImageLayer, 'id' | 'type' | 'frame'>> = {}): ImageLayer {
  return {
    id,
    type: 'image',
    visible: true,
    locked: false,
    opacity: 1,
    frame,
    constraints: defaultConstraints(),
    source: { kind: 'placeholder' },
    fit: { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 },
    mask: { kind: 'none' },
    filters: { grayscale: false, sepia: 0, brightness: 0, contrast: 0, saturation: 0 },
    border: null,
    shadow: null,
    ...patch,
  }
}

export function createTextLayer(id: string, frame: Frame, patch: Partial<Omit<TextLayer, 'id' | 'type' | 'frame'>> = {}): TextLayer {
  return {
    id,
    type: 'text',
    visible: true,
    locked: false,
    opacity: 1,
    frame,
    constraints: defaultConstraints(),
    text: '',
    font: { familyId: '', family: undefined, weight: 400, italic: false, sizePt: 24, lineHeight: 1.2, letterSpacingEm: 0 },
    color: '#1A1A1A',
    align: 'left',
    verticalAlign: 'top',
    autoFit: 'shrink',
    stroke: null,
    shadow: null,
    ...patch,
  }
}

export function createStickerLayer(id: string, frame: Frame, assetId: string, patch: Partial<Omit<StickerLayer, 'id' | 'type' | 'frame'>> = {}): StickerLayer {
  return {
    id,
    type: 'sticker',
    visible: true,
    locked: false,
    opacity: 1,
    frame,
    constraints: defaultConstraints(),
    source: { assetId },
    tint: null,
    ...patch,
  }
}

export function createShapeLayer(id: string, frame: Frame, patch: Partial<Omit<ShapeLayer, 'id' | 'type' | 'frame'>> = {}): ShapeLayer {
  return {
    id,
    type: 'shape',
    visible: true,
    locked: false,
    opacity: 1,
    frame,
    constraints: defaultConstraints(),
    shape: 'rect',
    fill: '#FFFFFF',
    stroke: null,
    cornerRadiusMm: 0,
    ...patch,
  }
}

/** Leeres Dokument: ein Platzhalter-Bildlayer über das volle Format. */
export function createEmptyDocument(canvas: DesignCanvas, generator = 'pfe-design-renderer'): DesignDocument {
  return {
    schemaVersion: DESIGN_SCHEMA_VERSION,
    canvas,
    layers: [createImageLayer('l1', fullBleedFrame(canvas), { constraints: defaultConstraints({ role: 'full-bleed', optional: false }) })],
    meta: { templateId: null, templateVersion: null, generator },
  }
}

export function cloneDocument<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}
